import { createEventDispatcher } from "svelte";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line camelcase
import { get_current_component } from "svelte/internal";

/**
 * Contains property changed detail for the event.
 * field names are equal to property names. If a prop name does not exist in the detail
 * this means the prop has not been changed.
 *
 * So the data will contain prop data only for changed props
 */
export type PropertyChangeDetail<Props = Record<string, unknown>> = {
  [P in keyof Props]?: {
    /** Property value before change */
    previous: Props[P];
    /** Proprty value after change */
    current: Props[P];
  };
};

type AnyPropertyChangeDetail = PropertyChangeDetail<Record<string, unknown>>;

export type PropertyChangeEvent<Props = Record<string, unknown>> = CustomEvent<
  PropertyChangeDetail<Props>
>;

/**
 *
 */
export type PropertyChangeListener<Props = Record<string, unknown>> = (
  this: HTMLElement,
  ev: PropertyChangeEvent<Props>,
) => void;

/**
 * This function tracks changes of all properties.
 * This hook function dispatches an event with name 'property-change' and
 * payload as the changes in the form of an object of key-value pairs.
 * The key is the property name and the value is an object with two properties:
 * 'previous' and 'current' referring to the previous and current values of the property.
 *
 * Usage: Just call `usePropertyChange()` within your svelte component.
 */
export function usePropertyChange(): void {
  const dispatch = createEventDispatcher<{ "property-change": AnyPropertyChangeDetail }>();

  /** do not dispatch prop changed during compoentn creation */
  let initialized = false;

  /** keep track of property values between updates of the component state */
  const lastState: Record<string, unknown> = {};
  const component = get_current_component();

  component.$$.after_update.push(() => {
    const { props, ctx } = component.$$;

    // Fill details with changed prop data
    const changes: AnyPropertyChangeDetail = {};
    Object.entries(props).forEach(([key, ctxIndex]) => {
      if (lastState[key] === ctx[ctxIndex]) return;

      // Only create details for changed props
      changes[key] = { previous: lastState[key], current: ctx[ctxIndex] };
      lastState[key] = ctx[ctxIndex];
    });

    // Do not dispatch the event on creation. since it will always fire with full set of props
    // Nobody needs it
    if (!initialized) {
      initialized = true;
      return;
    }

    // Once initialized, dispatch event with changed props details
    dispatch("property-change", changes);
  });
}
